import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getListStudentLedEnrollments } from 'modules/simulatorTrainings/common/api/simulatorExerciseService'
import { listStudentLedEnrollmentsLoaded } from 'modules/simulatorTrainings/common/slices/student'
import { SimulatorStudentLedEnrolledPerson } from 'modules/simulatorTrainings/common/types/student'
import { assessSimulatorExerciseScheduleParticipants } from 'modules/simulatorTrainings/instructor/studentLed/TrainingDetails/api/trainingDetailsService'
import { AssessmentRecord } from 'modules/simulatorTrainings/instructor/studentLed/TrainingDetails/types'
import { TrainingDetailsState, ApiAssessParticipantsResponse } from './types'

const initialState: TrainingDetailsState = {
  selectedAssessments: {},
  assessParticipantsRequest: {},
}

const trainingDetailsSlice = createSlice({
  name: 'simulatorTrainingInstructorSLTrainingDetails',
  initialState,
  reducers: {
    assessScheduleParticipantsRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiAssessParticipantsResponse>>
    ) {
      state.assessParticipantsRequest = action.payload
    },
    changeAssessments(state, action: PayloadAction<AssessmentRecord>) {
      state.selectedAssessments = action.payload
    },
    clearSelectedAssessments(state) {
      state.selectedAssessments = initialState.selectedAssessments
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { assessScheduleParticipantsRequested, changeAssessments, clearSelectedAssessments } =
  trainingDetailsSlice.actions
export { changeAssessments, clearSelectedAssessments }
export default trainingDetailsSlice.reducer

export const cleanUpEnrollmentsChanges =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    dispatch(listStudentLedEnrollmentsLoaded(response))
    if (!response.error) {
      dispatch(clearSelectedAssessments())
    }
  }

export const reloadListStudentLedEnrollmentsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListStudentLedEnrollments(scheduleId),
    },
    payloadAction: listStudentLedEnrollmentsLoaded,
    nextAction: cleanUpEnrollmentsChanges,
    getResultToKeep: ({ simulatorTrainingStudent }) =>
      simulatorTrainingStudent.listStudentLedEnrollmentsData.result,
  })
}

export const getInitialAssessments = createSelector(
  (students: SimulatorStudentLedEnrolledPerson[] = []) => students,
  students => {
    const result: AssessmentRecord = {}
    students.forEach(({ userId, status }: SimulatorStudentLedEnrolledPerson) => {
      result[userId] = status
    })
    return result
  }
)

export const updateListStudentLedEnrollments =
  (response: ApiMiddlewarePayload, params: { scheduleId: Guid }): AppThunk =>
  async dispatch => {
    const { scheduleId } = params
    dispatch(assessScheduleParticipantsRequested(response))
    if (!response.error) {
      dispatch(reloadListStudentLedEnrollmentsData(scheduleId) as MiddlewareApiAction)
    }
  }

export const assessScheduleParticipants = (
  scheduleId: Guid,
  selectedAssessments: AssessmentRecord
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => assessSimulatorExerciseScheduleParticipants(scheduleId, selectedAssessments),
    },
    payloadAction: assessScheduleParticipantsRequested,
    nextAction: {
      action: updateListStudentLedEnrollments,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
